import { createSelector } from 'reselect';
import {
  currentClientIdSelector,
  showPortfolioReportsSelector,
  isFinancialPlanEnabledSelector,
  selectIsHiddenLeversRTQ,
  selectHasAccessToProfile,
} from '../../CommonSelectors/CommonAreaSelector';
import { currentRouteSelector } from '../../CommonComponents/Router/Selector';

const getProfileLinks = (clientId, isHiddenLeversRTQ, isProfileEnabled) =>
  isProfileEnabled
    ? [
        {
          label: 'Personal Info',
          controller: 'Profile',
          params: { clientId, pivotId: 0, pageId: 'personalinfo' },
        },
        { label: 'Income', controller: 'ProfileIncome', params: { clientId } },
        { label: 'Expenses', controller: 'ProfileExpenses', params: { clientId, pivotId: 0 } },
        {
          label: 'Goals Input',
          controller: 'Profile',
          params: { clientId, pivotId: 0, pageId: 'goals' },
        },
        { label: 'Accounts', controller: 'ProfileAccounts', params: { clientId } },
        {
          label: 'Other Assets',
          controller: 'Profile',
          params: { clientId, pivotId: 0, pageId: 'otherassets' },
        },
        { label: 'Insurance', controller: 'ProfileInsurance', params: { clientId, pivotId: 0 } },
        {
          label: 'Equity Compensation',
          controller: 'ProfileStockOptions',
          params: { clientId, pivotId: 0 },
        },
        {
          label: 'Taxes',
          controller: 'Profile',
          params: { clientId, pivotId: 0, pageId: 'taxfiling' },
        },
        {
          label: isHiddenLeversRTQ ? 'Risk Profile' : 'Risk Tolerance',
          controller: 'ProfileRiskTolerance',
          params: { clientId, pivotId: 0 },
        },
      ]
    : [
        {
          label: 'Personal Info',
          controller: 'Profile',
          params: { clientId, pivotId: 0, pageId: 'personalinfo' },
        },

        { label: 'Accounts', controller: 'ProfileAccounts', params: { clientId } },
      ];

const getReportsLinks = clientId => [
  {
    label: 'Cash Flow',
    controller: 'ProjectedCashFlowReport',
    params: { clientId, mode: '1' },
  },
  {
    label: 'Balance Sheet',
    controller: 'ProjectedBalanceSheet',
    // eslint-disable-next-line camelcase
    params: { clientId, is_projected: '0' },
  },
  {
    label: 'Advanced Reports',
    controller: 'AdvancedReports',
    params: { clientId, mode: '' },
  },
  {
    label: 'Asset Allocation',
    controller: 'ReactAssetAllocationReport',
    params: { clientId },
  },
  {
    label: 'Action Steps',
    controller: 'TasksV2',
    params: { clientId },
  },
  {
    label: 'Portfolio Reports',
    controller: 'PortfolioReports',
    params: { clientId },
  },
];

export const sectionTitleSelector = createSelector(currentRouteSelector, ({ controller }) => {
  switch (controller) {
    case 'ProjectedCashFlowReport':
    case 'ProjectedBalanceSheet':
    case 'AdvancedReports':
    case 'TasksV2':
    case 'ReactAssetAllocationReport':
    case 'PortfolioReports':
      return 'Reports';
    case 'ProfileAccounts':
    case 'ProfileStockOptions':
    case 'ProfileInsurance':
    case 'ProfileLifeInsurance':
    case 'ProfileExpenses':
    case 'ProfileIncome':
    case 'ProfileRiskTolerance':
    case 'Profile':
      return 'Profile';
    default:
      return '';
  }
});

const isActiveMapper = (controller, pageId) => link => ({
  ...link,
  isActive: controller === link.controller && pageId === link.params.pageId,
});

export const linksSelector = createSelector(
  currentClientIdSelector,
  sectionTitleSelector,
  isFinancialPlanEnabledSelector,
  showPortfolioReportsSelector,
  currentRouteSelector,
  selectIsHiddenLeversRTQ,
  selectHasAccessToProfile,
  (
    clientId,
    sectionTitle,
    isFinancialPlanEnabled,
    showPortfolioReports,
    { controller, params: { pageId } },
    isHiddenLeversRTQ,
    hasAccessToProfile,
  ) => {
    switch (sectionTitle) {
      case 'Profile':
        return getProfileLinks(
          clientId,
          isHiddenLeversRTQ,
          isFinancialPlanEnabled && hasAccessToProfile,
        ).map(isActiveMapper(controller, pageId));
      case 'Reports': {
        // Show only Portfolio Reports
        if (showPortfolioReports && !isFinancialPlanEnabled) {
          return [
            {
              label: 'Portfolio Reports',
              controller: 'PortfolioReports',
              params: { clientId },
              isActive: true,
            },
          ];
        }

        return getReportsLinks(clientId)
          .map(isActiveMapper(controller, pageId))
          .filter(link => {
            if (link.controller === 'PortfolioReports') {
              return showPortfolioReports;
            }

            return true;
          });
      }
      default:
        return [];
    }
  },
);

export default linksSelector;
